<template>
  <div :class="isAuthPage ? 'auth-app-wrap' : (isFullscreenEmbed ? 'fullscreen-embed-wrap' : ['app', appClasses])" @contextmenu.prevent>
    <FuncBar v-if="!isAuthPage && !isFullscreenEmbed" />
    <!-- router-view spans columns 2-3, MessagesView internally renders SessionsBar+MainPanel via display:contents -->
    <router-view />
    <TableViewerView v-if="tableViewerMounted" v-show="$route.name === 'table-viewer'" />
    <MarkdownEditorView v-if="mdEditorMounted" v-show="$route.name === 'markdown-editor'" />
    <!-- DevView 持久化挂载：嵌入 Studio iframe，切走只 v-show=false，dom 不 detach，iframe 状态完整保留 -->
    <DevView v-if="!isAuthPage && devMounted" v-show="$route.name === 'dev'" />

    <!-- Overlays only shown when in main app (not auth/setup/fullscreen-embed) -->
    <template v-if="!isAuthPage && !isFullscreenEmbed">
    <!-- Workspace float overlay (only relevant on messages route) -->
    <div class="workspace-float-overlay" id="workspaceFloatOverlay"
      v-show="workspaceFloat || groupCloudFloat"
      :aria-hidden="!workspaceFloat && !groupCloudFloat"
      :class="{ active: workspaceFloat || groupCloudFloat }">
      <div class="workspace-float-scrim" @click="handleFloatScrimClick"></div>
      <div class="workspace-float-stage" id="workspaceFloatStage">
        <WorkspaceBrowserShell v-if="workspaceFloat || groupCloudFloat" :fullscreen="true" />
      </div>
    </div>
    <!-- Group dialog overlay -->
    <div class="group-dialog-overlay" id="groupDialogOverlay" :class="{ open: groupDialogOpen }" :aria-hidden="!groupDialogOpen">
      <div class="group-dialog-scrim" @click="closeGroupDialog"></div>
      <GroupDialog v-if="groupDialogOpen" />
    </div>
    <!-- Agent insight modal -->
    <AgentInsightModal v-if="agentInsightOpen" />
    <!-- Agent task modal -->
    <AgentTaskModal v-if="agentTaskPreviewOpen" />
    <!-- Agent detail popover -->
    <AgentDetailPopover v-if="agentDetailOpen" />
    <!-- Skill picker popover -->
    <SkillPickerPopover v-if="skillPickerOpen" />
    <!-- Model picker popover -->
    <ModelPickerPopover v-if="modelPickerOpen" />
    <!-- Telegram context menus -->
    <TelegramMenus />

    <!-- VNC 弹窗（挂载到 document.body，彻底脱离层叠上下文） -->
    <VncModal v-if="vncModal.open" />

    <!-- 全局 iframe 弹窗（Teable 表格内嵌，对话页 / 协作页共用） -->
    <div v-if="iframeModalState.open" class="g-iframe-overlay">
      <div class="g-iframe-panel">
        <div class="g-iframe-head">
          <div class="g-iframe-title">{{ iframeModalState.title }}</div>
          <div class="g-iframe-actions">
            <button type="button" class="g-iframe-action" title="在新标签页打开" @click="openIframeInNewTab">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <button type="button" class="g-iframe-close" @click="closeIframeModal">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
            </button>
          </div>
        </div>
        <div class="g-iframe-body">
          <div v-if="iframeLoading" class="g-iframe-loading">
            <div class="g-iframe-spinner"></div>
            <span>正在加载…</span>
          </div>
          <iframe :src="iframeModalState.url" class="g-iframe-frame" frameborder="0" allow="clipboard-read; clipboard-write" allowfullscreen @load="iframeLoading = false"></iframe>
        </div>
      </div>
    </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { initWs, destroyWs } from './utils/ws'
import { initPushSubscription, listenPushMessages, handleNotificationDeepLink } from './utils/push'
import FuncBar from './components/FuncBar.vue'
import WorkspaceBrowserShell from './components/WorkspaceBrowserShell.vue'
import GroupDialog from './components/dialogs/GroupDialog.vue'
import AgentInsightModal from './components/dialogs/AgentInsightModal.vue'
import AgentTaskModal from './components/dialogs/AgentTaskModal.vue'
import AgentDetailPopover from './components/AgentDetailPopover.vue'
import SkillPickerPopover from './components/SkillPickerPopover.vue'
import ModelPickerPopover from './components/ModelPickerPopover.vue'
import TelegramMenus from './components/TelegramMenus.vue'
import VncModal from './components/VncModal.vue'

export default {
  name: 'App',
  components: {
    FuncBar,
    WorkspaceBrowserShell,
    GroupDialog,
    AgentInsightModal,
    AgentTaskModal,
    AgentDetailPopover,
    SkillPickerPopover,
    ModelPickerPopover,
    TelegramMenus,
    VncModal,
    TableViewerView: () => import('./views/TableViewerView.vue'),
    MarkdownEditorView: () => import('./views/MarkdownEditorView.vue'),
    DevView: () => import('./views/DevView.vue')
  },
  computed: {
    ...mapGetters(['appClasses']),
    isAuthPage() {
      const name = this.$route ? this.$route.name : ''
      // setup-pending / setup-rejected 也算"认证页":不渲染 FuncBar 等主应用外壳,
      // 否则遮罩毛玻璃下会透出侧边栏
      return ['auth', 'setup-employee', 'setup-assistant', 'setup-pending', 'setup-rejected'].includes(name)
    },
    // 路由 query 带 fullscreen=1 时藏掉 FuncBar/浮层，让 router-view 独占视口
    // md 编辑器新标签页打开时用这个模式（表格走 iframe 自带独立 URL，无须）
    isFullscreenEmbed() {
      return !!(this.$route && this.$route.query && this.$route.query.fullscreen === '1')
    },
    ...mapState({
      workspaceFloat: s => s.workspaceFloat,
      workspaceTakeover: s => s.workspaceTakeover,
      groupCloudFloat: s => s.groupCloudFloat,
      groupDialogOpen: s => s.groupDialog.open,
      agentInsightOpen: s => s.agentInsightModal.open,
      agentTaskPreviewOpen: s => s.agentTaskPreview.open,
      agentDetailOpen: s => s.agentDetailPopover.open,
      skillPickerOpen: s => s.skillPickerOpen,
      modelPickerOpen: s => s.modelPickerOpen,
      vncModal: s => s.vncModal,
      iframeModalState: s => s.iframeModal,
      totalUnread: s => s.totalUnread
    }),
    currentRoute() {
      return this.$route ? this.$route.name : 'messages'
    }
  },
  data() {
    return {
      iframeLoading: true,
      tableViewerMounted: false,
      mdEditorMounted: false,
      devMounted: false,
      _faviconTimer: null,    // 标签页未读闪烁定时器
      _faviconFlashOn: false  // 当前闪烁帧
    }
  },
  methods: {
    ...mapActions(['closeWorkspaceFullscreen', 'closeGroupCloudFloat', 'initApp']),
    // ── 浏览器标签页未读提醒(微信网页版式:标题显示条数,后台时 favicon 闪烁)──
    _setFavicon(href) {
      const link = document.querySelector('link[rel="icon"]')
      if (link) link.href = href
    },
    // 动态生成「纯红圆 + 白色未读数 N」的 favicon dataURL(>99 显示 99+)
    _renderUnreadFavicon(n) {
      try {
        const size = 64
        const canvas = document.createElement('canvas')
        canvas.width = size; canvas.height = size
        const ctx = canvas.getContext('2d')
        // 整个 favicon 区域填红色圆(去掉 logo,纯红点)
        ctx.fillStyle = '#FF3B30'
        ctx.beginPath()
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
        ctx.fill()
        // 白色数字居中
        const label = n > 99 ? '99+' : String(n)
        const fontPx = label.length >= 3 ? size * 0.42 : size * 0.62
        ctx.font = '900 ' + fontPx + 'px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif'
        ctx.fillStyle = '#fff'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(label, size / 2, size / 2 + 2)
        return canvas.toDataURL('image/png')
      } catch (_) { return '' }
    },
    _startFaviconFlash() {
      if (this._faviconTimer) return
      // 立即先切到红点那帧,不用等 800ms
      const n0 = this.totalUnread || 0
      if (n0 <= 0) return
      this._faviconFlashOn = true
      this._setFavicon(this._renderUnreadFavicon(n0) || '/favicon.svg')
      // 每帧都实时读 this.totalUnread 重新画,数字跟未读数实时同步
      this._faviconTimer = setInterval(() => {
        this._faviconFlashOn = !this._faviconFlashOn
        const n = this.totalUnread || 0
        if (n <= 0) { this._stopFaviconFlash(); return }
        this._setFavicon(this._faviconFlashOn ? (this._renderUnreadFavicon(n) || '/favicon.svg') : '/favicon.svg')
      }, 800)
    },
    _stopFaviconFlash() {
      if (this._faviconTimer) { clearInterval(this._faviconTimer); this._faviconTimer = null }
      this._faviconFlashOn = false
      this._setFavicon('/favicon.svg')
    },
    _updateTabNotify() {
      const n = this.totalUnread || 0
      const base = 'ARP AgentOS'
      // 标题:有未读直接显示"N 条新消息 · ARP AgentOS"(>99 显示 99+);读完恢复
      const label = n > 99 ? '99+' : n
      document.title = n > 0 ? `${label} 条新消息 · ${base}` : base
      // favicon 闪烁:只看有没有未读,不再看 document.hidden(前后台都闪)
      if (n > 0) this._startFaviconFlash()
      else this._stopFaviconFlash()
    },
    openIframeInNewTab() {
      if (this.iframeModalState.url) window.open(this.iframeModalState.url, '_blank')
    },
    closeIframeModal() {
      this.iframeLoading = true
      this.$store.commit('CLOSE_IFRAME_MODAL')
    },
    _requestNotificationPermission() {
      if (typeof Notification === 'undefined') return
      if (Notification.permission === 'default') {
        // 延迟请求，避免页面加载时立刻弹出
        setTimeout(() => {
          Notification.requestPermission()
        }, 3000)
      }
    },
    handleFloatScrimClick() {
      if (this.groupCloudFloat) this.closeGroupCloudFloat()
      else this.closeWorkspaceFullscreen()
    },
    handleFloatCloseClick() {
      if (this.groupCloudFloat) this.closeGroupCloudFloat()
      else this.closeWorkspaceFullscreen()
    },
    closeGroupDialog() {
      this.$store.commit('SET_GROUP_DIALOG', { open: false })
    },
    handleKeydown(event) {
      if (event.key !== 'Escape') return
      if (this.iframeModalState.open) { this.closeIframeModal(); return }
      if (this.groupCloudFloat) { this.closeGroupCloudFloat(); return }
      if (this.workspaceFloat) { this.closeWorkspaceFullscreen(); return }
      if (this.agentInsightOpen) { this.$store.dispatch('closeAgentInsightModal'); return }
      if (this.agentTaskPreviewOpen) { this.$store.dispatch('closeAgentTaskPreview'); return }
      if (this.agentDetailOpen) { this.$store.dispatch('closeAgentDetailPopover'); return }
      if (this.skillPickerOpen) { this.$store.commit('SET_SKILL_PICKER_OPEN', false); return }
      if (this.modelPickerOpen) { this.$store.commit('SET_MODEL_PICKER_OPEN', false) }
    }
  },
  mounted() {
    this.$store.dispatch('initApp')
    window.addEventListener('keydown', this.handleKeydown)
    if (this.$route) {
      this.$store.commit('SET_CURRENT_VIEW', this.$route.name || 'messages')
      if (this.$route.name === 'table-viewer') this.tableViewerMounted = true
      if (this.$route.name === 'markdown-editor') this.mdEditorMounted = true
      if (this.$route.name === 'dev') this.devMounted = true
    }
    // 非认证页面才建立 WebSocket 和拉取通知设置
    if (!this.isAuthPage) {
      initWs(this.$store)
      this.$store.dispatch('fetchNotificationSettings')
      this.$store.dispatch('fetchHotkeySettings')
      this._requestNotificationPermission()
      // Web Push 离线推送
      initPushSubscription()
      listenPushMessages(this.$store)
    }
    // 暴露 router 供 push.js 通知点击跳转用
    window.__vueRouter = this.$router
    // 由通知冷启动进来时，按 URL ?conversationId= 直达对应会话（需在 __vueRouter 赋值后）
    if (!this.isAuthPage) handleNotificationDeepLink(this.$store)
    // 标签页未读提醒:切回前台时停闪烁(标题仍保留条数,直到真正读完 totalUnread 归零)
    this._onVisChange = () => this._updateTabNotify()
    document.addEventListener('visibilitychange', this._onVisChange)
    this._updateTabNotify()
  },
  watch: {
    totalUnread() { this._updateTabNotify() },
    isAuthPage(val) {
      if (!val) {
        initWs(this.$store)
        this.$store.dispatch('fetchNotificationSettings')
        this._requestNotificationPermission()
        initPushSubscription()
        listenPushMessages(this.$store)
      } else {
        destroyWs()
        // 退出登录 / 进入认证页：彻底销毁持久化嵌入(Studio 开发页 / Teable 表格 / md 编辑器)，
        // 否则换账号后这些 iframe 复用上个账号的会话，导致账号信息串联
        this.devMounted = false
        this.tableViewerMounted = false
        this.mdEditorMounted = false
      }
    },
    // Keep store in sync with route for CSS class on .app
    '$route.name'(name, oldName) {
      if (name === 'table-viewer') this.tableViewerMounted = true
      if (name === 'markdown-editor') this.mdEditorMounted = true
      if (name === 'dev') this.devMounted = true
      this.$store.commit('SET_CURRENT_VIEW', name || 'messages')
      if (name === 'messages' && oldName && oldName !== 'messages') {
        const item = this.$store.state.sessionItems.find(s => s.key === this.$store.state.currentSessionKey)
        if (item && item.type === 'agent' && item.conversationId) {
          this.$store.dispatch('fetchAgentCapabilitiesAndSkills', item.conversationId)
        }
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown)
    if (this._onVisChange) document.removeEventListener('visibilitychange', this._onVisChange)
    this._stopFaviconFlash()
    destroyWs()
  }
}
</script>
